import { Link, useSearchParams } from 'react-router';
import { MIGRATION_STATES, type MigrationDTO } from '@devrobot/shared';
import { ArrowRightLeft, Plus } from 'lucide-react';
import { useMigrations, useProjects } from '../../api/queries';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconChip, PageHeader } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonTable } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { KindBadge, SourceToDestination } from './PlanSummary';

const HEAD = 'text-xs tracking-wide text-base-content/50 uppercase';

function NewButton({ primary = true }: { primary?: boolean }) {
  return (
    <Link to="/migrations/new" className={`btn btn-sm ${primary ? 'btn-primary' : 'btn-soft btn-primary'}`}>
      <Plus className="size-4" aria-hidden="true" /> New migration
    </Link>
  );
}

function MigrationList({ migrations }: { migrations: MigrationDTO[] }) {
  return (
    <div className="card dr-surface min-w-0 overflow-hidden">
      <ul className="divide-y divide-hairline md:hidden" aria-label="Migrations">
        {migrations.map((m) => (
          <li key={m.id} className="space-y-1.5 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <Link to={`/migrations/${m.id}`} className="min-w-0 font-semibold break-anywhere hover:underline">
                {m.project.name}
              </Link>
              <StatusBadge kind="migration" value={m.state} size="xs" className="mt-0.5 shrink-0" />
            </div>
            <div className="text-sm">
              <SourceToDestination source={m.source} destination={m.destination} />
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/60">
              <KindBadge kind={m.kind} size="xs" />
              <span>by {m.createdBy}</span>
              <span aria-hidden="true">·</span>
              <RelativeTime value={m.createdAt} />
            </div>
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto max-md:hidden">
        <table className="table table-sm">
          <thead>
            <tr className={HEAD}>
              <th>Project</th>
              <th>Kind</th>
              <th>Source → destination</th>
              <th>State</th>
              <th className="max-lg:hidden">Created by</th>
              <th>Created</th>
              <th className="max-lg:hidden">Finished</th>
            </tr>
          </thead>
          <tbody>
            {migrations.map((m) => (
              <tr key={m.id} className="transition-colors duration-150 hover:bg-base-200/50">
                <td>
                  <Link to={`/migrations/${m.id}`} className="font-semibold hover:underline">
                    {m.project.name}
                  </Link>
                  <div className="font-mono text-xs text-base-content/55">{m.project.key}</div>
                </td>
                <td>
                  <KindBadge kind={m.kind} size="xs" />
                </td>
                <td className="text-sm">
                  <SourceToDestination source={m.source} destination={m.destination} />
                </td>
                <td>
                  <StatusBadge kind="migration" value={m.state} />
                </td>
                <td className="text-sm max-lg:hidden">{m.createdBy}</td>
                <td className="text-sm whitespace-nowrap text-base-content/65">
                  <RelativeTime value={m.createdAt} />
                </td>
                <td className="text-sm whitespace-nowrap text-base-content/65 max-lg:hidden">
                  <RelativeTime value={m.finishedAt} fallback="—" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MigrationsPage() {
  const [params, setParams] = useSearchParams();
  const project = params.get('project') ?? '';
  const state = params.get('state') ?? '';
  const q = useMigrations({ project: project || undefined, state: state || undefined });
  const projects = useProjects();
  const filtered = Boolean(project || state);

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        icon={<IconChip icon={ArrowRightLeft} tone="primary" size="lg" />}
        title="Migrations"
        subtitle="Move a deployment’s databases, volumes and configuration to another VPS, or copy them into another environment of the same project."
        actions={<NewButton />}
      />
      <form className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3" onSubmit={(e) => e.preventDefault()} aria-label="Filter migrations">
        <select className="select select-sm w-auto" aria-label="Project" value={project} onChange={(e) => set('project', e.target.value)}>
          <option value="">All projects</option>
          {(projects.data ?? []).map((p) => (
            <option key={p.key} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
        <select className="select select-sm w-auto" aria-label="State" value={state} onChange={(e) => set('state', e.target.value)}>
          <option value="">All states</option>
          {MIGRATION_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {filtered ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setParams({}, { replace: true })}>
            Clear filters
          </button>
        ) : null}
        {q.isFetching && !q.isPending ? <span className="loading loading-spinner loading-xs text-base-content/50 sm:ml-auto" aria-label="Refreshing" /> : null}
      </form>
      {q.isPending ? <SkeletonTable /> : null}
      {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? (
        filtered ? (
          <EmptyState icon={ArrowRightLeft} title="No migrations match these filters">
            Clear the filters to see all migrations.
          </EmptyState>
        ) : (
          <EmptyState icon={ArrowRightLeft} title="No migrations yet" action={<NewButton primary={false} />}>
            A migration copies a deployment’s databases, volumes and configuration to a new home: the same environment on another VPS (a server move), or another environment of the project (for example
            production data into staging). A guided wizard makes you back up first, shows the exact plan and asks for confirmation before anything changes.
          </EmptyState>
        )
      ) : null}
      {q.data && q.data.length > 0 ? <MigrationList migrations={q.data} /> : null}
    </>
  );
}
